import keys from 'lodash/keys';
import CustomStore from 'devextreme/data/custom_store';
import { Queries } from './useQueries';
import { QueryListParams, SortDirection } from '#/graphql/types';
import { transformDateTimeValue } from '#/common/utils/datetimeTransform';
import DataStoreOptions from './DataStoreOptions';
import deepMap from '#/common/utils/deepMap';
import { NULL_STR } from '#/config/consts';

const createDataStore = <T>(queries: Queries<T>, options: DataStoreOptions<T>) => {
  const { columns, cacheEnabled, key = 'id', searchKey } = options;
  const { getItem, getItemList } = queries;

  let cache: T[] = [];

  return new CustomStore<T, string>({
    key,
    byKey: async (id) => {
      if (!id) {
        return null as T;
      }
      try {
        if (cacheEnabled && cache.length > 0) {
          const cachedItem = cache.find((item) => item[id as keyof T] === id);
          if (cachedItem) {
            return cachedItem as T;
          }
        }
        const response = await getItem(id);
        const queryName = keys(response.data)[0];
        const result = response.data[queryName];

        if (result.status === 404) {
          return null as T;
        }

        if (result.status !== 200) {
          throw new Error(response.errors?.join('\n'));
        }

        return result.data as T;
      } catch (err) {
        console.error(err);
        throw new Error('Data Loading Error');
      }
    },
    load: async (loadOptions) => {
      try {
        const sortOptions: any = Array.isArray(loadOptions.sort)
          ? loadOptions.sort[0]
          : loadOptions.sort;

        const oldFilter = loadOptions.filter || [];
        let filter = deepMap<string | null>(oldFilter, (value) =>
          value === NULL_STR ? null : value,
        );

        if (loadOptions.searchValue) {
          if (filter.length > 0) {
            filter.push('AND');
          }

          filter.push([
            searchKey || loadOptions.searchExpr,
            loadOptions.searchOperation,
            loadOptions.searchValue,
          ]);
        }

        if (options.filter) {
          if (filter.length > 0) {
            if (!Array.isArray(filter[0])) {
              // ['column', 'operator', value] -> [['column', 'operator', value]]
              filter = [filter];
            }
            filter.push('AND');
          }
          // Push the whole array if it does not contain nested arrays and we have a searchValue
          if (
            filter.length > 0 &&
            Array.isArray(options.filter) &&
            !options.filter.every((innerFilter) => Array.isArray(innerFilter))
          ) {
            filter.push(options.filter);
          } else {
            // Otherwise flatten the array or push each nested array individually
            options.filter.forEach((item) => {
              if (Array.isArray(item) && item.every((innerItem) => Array.isArray(innerItem))) {
                item.forEach((innerArray, index) => {
                  if (index > 0) {
                    filter.push('AND');
                  }
                  filter.push(innerArray);
                });
              } else {
                filter.push(item);
              }
            });
          }
        }

        const sortField =
          typeof sortOptions?.selector === 'string'
            ? sortOptions.selector
            : sortOptions?.selector?.context?.column?.dataField;

        const params: QueryListParams = {
          filter: filter ? JSON.stringify(filter) : null,
          sortBy: sortField
            ? {
                field: sortField,
                direction: sortOptions.desc ? SortDirection.DESC : SortDirection.ASC,
              }
            : null,
          skip: loadOptions.skip ?? null,
          take: loadOptions.take ?? null,
          requireTotalCount: loadOptions.requireTotalCount ?? null,
          ...(options.accessTo ? { accessTo: options.accessTo } : {}),
        };

        const response = await getItemList(params);
        const queryName = keys(response.data)[0];
        const result = response.data[queryName];

        if (result.status !== 200) {
          throw new Error(response.errors?.join('\n'));
        }

        // TODO: Remove this after fixing the datetime values on the backend
        const transformDataBasedOnColumns = (data: any[]): any[] => {
          if (!columns || !data) return data;

          // Get all columns that have dataType: 'date'
          const dateColumns = columns
            .filter((col) => col.dataType === 'date')
            .map((col) => col.dataField);

          return data.map((item) => {
            const transformed = { ...item };

            dateColumns.forEach((fieldName) => {
              if (fieldName in transformed) {
                transformed[fieldName] = transformDateTimeValue(transformed[fieldName]);
              }
            });

            columns
              .map((col) => col.dataField)
              .forEach((fieldName) => {
                if (fieldName in transformed && transformed[fieldName] === null) {
                  transformed[fieldName] = NULL_STR;
                }
              });

            return transformed;
          });
        };

        const transformedData = transformDataBasedOnColumns(result.data ?? []);

        if (cacheEnabled) {
          cache = transformedData;
        }

        return {
          data: transformedData,
          totalCount: result.totalCount,
        };
      } catch (err) {
        console.error(err);
        throw new Error('Data Loading Error');
      }
    },
  });
};

export default createDataStore;
